import React, {useEffect, useMemo, useState} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {IconButton} from "@mui/material";
import {GridColDef} from "@mui/x-data-grid";
import {toast} from "sonner";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {useGetSbtTemplates} from "../../apis/requests/sbt-templates/getSbtTemplates";
import {useRefreshSbtTemplates} from "../../apis/requests/sbt-templates/refreshSbtTemplates";
import {SbtTemplate} from "../../apis/requests/sbt-templates/types";
import {parseUtc, StatusBanner} from "../../components/StatusBanner";
import {useDebounce} from "../../hooks/useDebounce";
import {LIST_HEADER_HEIGHT, ListDataGrid, ListPageHeader, SearchInput} from "../../ui/components";
import {SbtTemplateDrawer} from "./SbtTemplateDrawer";
import {SbtTemplateTypeChips} from "./SbtTemplateTypeChips";

const DEFAULT_PAGE_SIZE = 100;
const STALE_AFTER_MS = 25 * 60 * 60 * 1000;
const REFRESHED_TITLE = "SBT templates";
const STALE_TITLE = "Refresh recommended";
const REFRESHING_TITLE = "Refreshing from SBT…";
const NOT_REFRESHED_TEXT = "Not refreshed yet";
const REFRESH_LABEL = "Refresh from SBT";

const ellipsisSx = {overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"} as const;

const GlossaryPage = () => {
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: DEFAULT_PAGE_SIZE});
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [selectedTemplate, setSelectedTemplate] = useState<SbtTemplate | null>(null);
  useEffect(() => {
    setPaginationModel((m) => ({...m, page: 0}));
  }, [debouncedSearch]);

  const {data, isLoading, isFetching, error} = useGetSbtTemplates(
    {paginationModel, search: debouncedSearch},
    {keepPreviousData: true}
  );
  const {mutate: refreshTemplates, isPending: isRefreshing} = useRefreshSbtTemplates();

  const refresh = () =>
    refreshTemplates(
      {},
      {
        onSuccess: (result) => {
          if (result.recently_refreshed) {
            toast.info("Already refreshed less than a minute ago");
            return;
          }
          if (result.failed_group_ids.length) {
            toast.error(`Couldn't refresh ${result.failed_group_ids.length} SBT group(s). Try again later.`);
            return;
          }
          toast.success(
            `Refreshed ${result.total} templates (${result.created} new, ${result.updated} changed, ${result.removed} removed)`
          );
        },
      }
    );

  const columns = useMemo<GridColDef<SbtTemplate>[]>(
    () => [
      {
        field: "code",
        headerName: "Code",
        width: 90,
        sortable: false,
        renderCell: ({row}) => (
          <Text size={13} sx={{color: Colors.grey._700, fontFamily: "monospace"}}>
            {row.code}
          </Text>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        sortable: false,
        renderCell: ({row}) => (
          <Text size={14} variant="inter400" sx={{color: Colors.grey._900, ...ellipsisSx}}>
            {row.name}
          </Text>
        ),
      },
      {
        field: "message",
        headerName: "Message",
        flex: 4,
        sortable: false,
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._700, ...ellipsisSx}}>
            {row.message}
          </Text>
        ),
      },
      {
        field: "is_first_party",
        headerName: "Type",
        flex: 1.5,
        sortable: false,
        renderCell: ({row}) => <SbtTemplateTypeChips template={row} />,
      },
      {
        field: "open",
        headerName: "",
        width: 56,
        sortable: false,
        renderCell: ({row}) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTemplate(row);
            }}
            sx={{color: Colors.grey._700}}
          >
            <ChevronRightIcon sx={{fontSize: 20}} />
          </IconButton>
        ),
      },
    ],
    []
  );

  const lastRefreshedAt = data?.last_refreshed_at ? parseUtc(data.last_refreshed_at) : null;
  const isStale = !lastRefreshedAt || Date.now() - lastRefreshedAt.getTime() > STALE_AFTER_MS;
  const title = isRefreshing ? REFRESHING_TITLE : isStale ? STALE_TITLE : REFRESHED_TITLE;
  const subtitle = lastRefreshedAt
    ? `Last refreshed ${lastRefreshedAt.toLocaleString()} · ${data?.templates_count.toLocaleString()} templates`
    : NOT_REFRESHED_TEXT;

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title="Glossary">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by code or text..." />
      </ListPageHeader>
      {data && (
        <StatusBanner
          title={title}
          subtitle={subtitle}
          isRunning={isRefreshing}
          isWarning={isStale}
          refreshLabel={REFRESH_LABEL}
          onRefresh={refresh}
          isRefreshing={isRefreshing}
        />
      )}
      {error ? (
        <Text size={14}>Failed to load SBT templates.</Text>
      ) : (
        <ListDataGrid
          rows={data?.items ?? []}
          columns={columns}
          loading={isLoading || isFetching}
          getRowId={(row) => `${row.group_id}-${row.code}`}
          onRowClick={({row}) => setSelectedTemplate(row)}
          paginationMode="server"
          rowCount={data?.total ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          columnHeaderHeight={LIST_HEADER_HEIGHT}
          noRowsText={debouncedSearch ? "No SBT templates match your search" : "No SBT templates yet"}
        />
      )}
      <SbtTemplateDrawer template={selectedTemplate} onClose={() => setSelectedTemplate(null)} />
    </Box>
  );
};

export default GlossaryPage;
